import { useRef } from 'react';
import PatientListTable from './PatientListTable';
import RosterToolbar from './RosterToolbar';
import ConditionFilter from './ConditionFilter';
import SubtrackFilter from './SubtrackFilter';
import BoardTiles from './BoardTiles';
import CmsGoalsSection from './CmsGoalsSection';
import AccessJourney from './AccessJourney';
import BoardTodoLine from './BoardTodoLine';
import RoleSwitcher from './RoleSwitcher';
import useDashboardTable from '../hooks/useDashboardTable';
import useStuckAtTop from '../hooks/useStuckAtTop';
import { boardColumns } from '../logic/dashboard';

export default function AccessDashboard({ dashboardData, allPatients, onSelectPatient, userRole, userName, onRoleChange }) {
  const {
    kpis, columns, includes, rows, pagination, loading,
    loadingMore, hasMore, loadMore,
    sort, searchInput,
    filters, selectedFilters,
    handleSortChange, handleSearchChange, handleFiltersChange, handleBoardPick,
    markRead,
  } = useDashboardTable(userRole, dashboardData, allPatients);

  // Clear removes its own button, so focus moves to the list it just widened.
  const rosterRef = useRef(null);
  const clearFromTodo = (pick) => {
    handleBoardPick(pick);
    rosterRef.current?.focus({ preventScroll: true });
  };
  // Drives the toolbar's pinned styling (full-width bar vs card header).
  const { ref: toolbarRef, stuck: toolbarStuck } = useStuckAtTop();

  return (
    <div>

      {/* Greeting + the viewing lens, switched in place. */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap',
        margin: '0 4px 28px',
      }}>
        {userName && (
          <div style={{ fontSize: 22, fontWeight: 600, lineHeight: 1.2, color: 'var(--wi-text-primary)' }}>
            Hello, {userName}
          </div>
        )}
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginLeft: 'auto' }}>
          <span className="detail1 text-secondary">Viewing as</span>
          <RoleSwitcher currentRole={userRole} onChange={onRoleChange} />
        </div>
      </div>

      <BoardTiles
        filters={filters}
        enrolled={kpis ? kpis.enrolled.count : null}
        selected={selectedFilters}
        onPick={handleBoardPick}
      />

      <CmsGoalsSection
        filters={filters}
        selected={selectedFilters.oap_outcome || []}
        onPick={handleBoardPick}
      />

      <AccessJourney
        filters={filters}
        selected={selectedFilters.open_task || []}
        selectedReady={selectedFilters.report_ready || []}
        userRole={userRole}
        loading={loading && !kpis}
        onPick={handleBoardPick}
      />

      {/* The toolbar is the card's header, but a SIBLING of it — the card's clip
          would kill its sticky. CSS gives it the card's top border + radius. */}
      <div ref={toolbarRef} className={`roster-toolbar-pinned${toolbarStuck ? ' is-stuck' : ''}`}>
        <RosterToolbar
          showSearch
          value={searchInput || ''}
          onChange={handleSearchChange}
          countLabel={pagination ? `${pagination.total_count} results` : null}
          // The clinician's list holds only their patients, so the bar says so. Once pinned, the
          // greeting row's role switcher has scrolled away, so the bar carries it too.
          trailing={(userRole === 'clinician' || toolbarStuck) ? (
            <>
              {userRole === 'clinician' && (
                <span className="detail1 text-secondary">Patients with an open clinician task</span>
              )}
              {toolbarStuck && (
                <>
                  <span className="detail1 text-secondary">Viewing as</span>
                  <RoleSwitcher currentRole={userRole} onChange={onRoleChange} />
                </>
              )}
            </>
          ) : null}
          extras={
            <>
              <SubtrackFilter
                filters={filters}
                selected={selectedFilters.subtrack || []}
                onChange={(next) => handleFiltersChange('subtrack', next)}
              />
              <ConditionFilter
                filters={filters}
                selectedFilters={selectedFilters}
                onChange={handleFiltersChange}
              />
            </>
          }
        />
      </div>

      <div ref={rosterRef} tabIndex={-1} role="region" aria-label="Patient list" style={{
        background: 'var(--wi-background-secondary)',
        // top edge belongs to the toolbar above (same card, two boxes)
        borderRadius: '0 0 12px 12px',
        border: '1px solid var(--wi-divider)', borderTop: 'none', overflow: 'hidden',
        outline: 'none',
      }}>
        <BoardTodoLine filters={filters} selected={selectedFilters} onPick={clearFromTodo} />
        <PatientListTable
          externalToolbar
          patients={rows}
          serverColumns={boardColumns(columns, includes, userRole)}
          serverPagination={pagination}
          tableLoading={loading}
          sort={sort}
          searchValue={searchInput}
          onSortChange={handleSortChange}
          onSearchChange={handleSearchChange}
          onLoadMore={loadMore}
          hasMore={hasMore}
          loadingMore={loadingMore}
          externalLoading={!dashboardData && !kpis}
          onRowClick={onSelectPatient ? (p) => onSelectPatient(p.userid) : undefined}
          onMarkRead={markRead}
        />
      </div>

    </div>
  );
}
